import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const GameContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  image-rendering: pixelated;
  z-index: 1; /* Lower z-index so chat elements can be on top */
  pointer-events: none; /* Allow clicks to pass through to elements below */
`;

const Plane = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  font-size: 20px;
  font-weight: bold;
  transform: rotate(${props => props.$rotation}deg);
  transition: transform 0.1s ease;
  z-index: 2;
  pointer-events: none; /* Allow clicks to pass through */

  &::after {
    content: '>';
    display: block;
    line-height: 1;
  }
`;

const Circle = styled.div`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  border: 1px solid var(--primary-color);
  background-color: ${props => props.$filled ? 'var(--primary-color)' : 'transparent'};
  opacity: ${props => props.$filled ? 0.8 : 0.4};
  image-rendering: pixelated;
  pointer-events: none; /* Allow clicks to pass through */
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background-color: var(--primary-color);
  width: ${props => props.$score}vw;
  transition: width 0.3s ease;
  pointer-events: none; /* Allow clicks to pass through */
`;

// Add a new styled component for a portal to render the touch controls outside the normal stacking context
const TouchControlsPortal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999; /* Very high z-index to ensure it's above everything */

  /* This ensures the portal doesn't interfere with other elements */
  & > * {
    pointer-events: auto;
  }
`;

// Update the TouchControls component
const TouchControls = styled.div.attrs({ className: 'plane-game-touch-controls' })`
  position: fixed;
  bottom: 180px; /* Moved even higher up to avoid suggestions */
  right: 20px;
  display: grid;
  grid-template-columns: repeat(3, 50px); /* Even larger for better touch targets */
  grid-template-rows: repeat(3, 50px); /* Even larger for better touch targets */
  gap: 5px;
  pointer-events: auto;
  opacity: 0.95; /* Almost fully opaque for better visibility */
  /* Ensure controls are always on top and clickable */
  isolation: isolate;
  transform: translateZ(0);
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.3)); /* Add shadow for better visibility */

  @media (min-width: 768px) {
    display: none; /* Hide on desktop */
  }
`;

const TouchButton = styled.button`
  width: 50px; /* Increased size for better touch targets */
  height: 50px; /* Increased size for better touch targets */
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.95); /* More opaque */
  border: 2px solid var(--primary-color); /* Thicker border */
  color: var(--primary-color);
  font-size: 24px; /* Larger font */
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2); /* Add shadow for better visibility */

  &:active {
    background-color: var(--primary-color);
    color: white;
    transform: scale(0.95); /* Slight scale effect when pressed */
  }
`;

// eslint-disable-next-line no-unused-vars
const ScoreDisplay = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 14px;
  color: var(--primary-color);
  font-weight: bold;
  font-family: monospace;
`;

const Firework = styled.div`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  background-color: var(--primary-color);
  opacity: ${props => props.$opacity};
  transform: translate(${props => props.$x}px, ${props => props.$y}px);
  image-rendering: pixelated;
  pointer-events: none; /* Allow clicks to pass through */
`;

// Update the gamepad icon with white background and primary color for border and + symbol
const GamepadIcon = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  pointer-events: auto;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  font-size: 28px;
  font-weight: bold;
  line-height: 0; /* Ensure vertical centering of the + symbol */
  padding-bottom: 4px; /* Fine-tune vertical alignment */

  &:active, &:hover {
    transform: scale(0.95);
    background-color: white; /* Keep background white on hover/active */
    color: var(--primary-color); /* Keep text primary color on hover/active */
    border-color: var(--primary-color); /* Ensure border stays primary color */
    opacity: 0.9; /* Slight opacity change for hover feedback */
  }

  /* Show only on mobile */
  @media (min-width: 768px) {
    display: none;
  }
`;

// Update the GameInstructions component to be hidden by default on mobile
const GameInstructions = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  font-size: 12px;
  color: var(--primary-color);
  opacity: 0.8;
  font-family: monospace;
  pointer-events: none; /* Allow clicks to pass through */

  /* Hide on mobile by default */
  @media (max-width: 767px) {
    opacity: ${props => props.$controlsVisible ? 0.8 : 0};
    transition: opacity 0.3s ease;
  }
`;

// eslint-disable-next-line no-unused-vars
const SpeedDisplay = styled.div`
  position: absolute;
  top: 30px;
  right: 10px;
  font-size: 12px;
  color: var(--primary-color);
  opacity: 0.8;
  font-family: monospace;
`;

// Invisible overlay to capture keyboard events only
const KeyboardEventCatcher = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: auto; /* Capture clicks by default */

  &:focus {
    outline: none;
  }
`;

const PlaneGame = () => {
  const [planePosition, setPlanePosition] = useState({ x: 50, y: 100 });
  const [planeRotation, setPlaneRotation] = useState(0);
  const [circles, setCircles] = useState([]);
  const [score, setScore] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [fireworks, setFireworks] = useState([]);
  const [gameSpeed, setGameSpeed] = useState(1);
  const [boostFactor, setBoostFactor] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTouchDirection, setActiveTouchDirection] = useState(null);
  const gameContainerRef = useRef(null);
  const keyboardCatcherRef = useRef(null);
  const animationFrameRef = useRef(null);
  const circleAnimationRef = useRef(null);
  const fireworkIntervalRef = useRef(null);
  const keyPressTimeRef = useRef(null);
  const touchIntervalRef = useRef(null);
  const [controlsVisible, setControlsVisible] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 ||
                 ('ontouchstart' in window) ||
                 (navigator.maxTouchPoints > 0));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize circles
  useEffect(() => {
    // Initial setup when component mounts
    if (gameContainerRef.current) {
      const { width, height } = gameContainerRef.current.getBoundingClientRect();
      const initialCircles = [];

      // Create initial circles
      for (let i = 0; i < 30; i++) {
        initialCircles.push({
          id: i,
          x: Math.random() * width + width / 2, // Start from middle to right side
          y: Math.random() * height,
          size: Math.floor(Math.random() * 3) * 4 + 4, // More pixelated sizes: 4, 8, or 12
          filled: Math.random() < 0.3, // 30% chance to be filled
          collected: false,
          speed: Math.random() * 2 + 1 // Base speed varies slightly
        });
      }

      setCircles(initialCircles);
    }

    // Cleanup animation frames on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const circleAnimationRefValue = circleAnimationRef.current;
      if (circleAnimationRefValue) {
        cancelAnimationFrame(circleAnimationRefValue);
      }
    };
  }, []);

  // Create a new circle
  const createNewCircle = (width, height, id) => {
    return {
      id: id || Date.now() + Math.random(),
      x: width + (Math.random() * 100), // Start just off the right edge
      y: Math.random() * height,
      size: Math.floor(Math.random() * 3) * 4 + 4, // More pixelated sizes: 4, 8, or 12
      filled: Math.random() < 0.3, // 30% chance to be filled
      collected: false,
      speed: Math.random() * 2 + 1 // Base speed varies slightly
    };
  };

  // Move plane based on direction
  const movePlane = (direction, boost = false) => {
    // Base speed that gets multiplied by the boost factor
    const baseSpeed = 8;
    const currentBoostFactor = boost ? 2 : 1;
    const boostedSpeed = baseSpeed * currentBoostFactor;

    switch (direction) {
      case 'up':
        setPlanePosition(prev => ({
          ...prev,
          y: Math.max(0, prev.y - boostedSpeed)
        }));
        setPlaneRotation(270);
        break;
      case 'down':
        setPlanePosition(prev => ({
          ...prev,
          y: Math.min(gameContainerRef.current?.clientHeight - 20 || window.innerHeight - 20, prev.y + boostedSpeed)
        }));
        setPlaneRotation(90);
        break;
      case 'left':
        setPlanePosition(prev => ({
          ...prev,
          x: Math.max(0, prev.x - boostedSpeed)
        }));
        setPlaneRotation(180);
        break;
      case 'right':
        setPlanePosition(prev => ({
          ...prev,
          x: Math.min(gameContainerRef.current?.clientWidth - 20 || window.innerWidth - 20, prev.x + boostedSpeed)
        }));
        setPlaneRotation(0);
        break;
      default:
        break;
    }
  };

  // Handle touch controls
  const handleTouchStart = (direction) => {
    setActiveTouchDirection(direction);
    setBoostFactor(1);
    keyPressTimeRef.current = Date.now();

    // Clear any existing interval
    if (touchIntervalRef.current) {
      clearInterval(touchIntervalRef.current);
    }

    // Move immediately on first touch
    movePlane(direction);

    // Then set up interval for continuous movement
    touchIntervalRef.current = setInterval(() => {
      // Calculate boost factor based on how long button has been pressed
      const pressDuration = Date.now() - keyPressTimeRef.current;
      const newBoostFactor = Math.min(3, 1 + Math.pow(pressDuration / 1000, 1.5) * 0.5);
      setBoostFactor(newBoostFactor);

      movePlane(direction, true);
    }, 50);
  };

  const handleTouchEnd = () => {
    if (touchIntervalRef.current) {
      clearInterval(touchIntervalRef.current);
    }
    setActiveTouchDirection(null);
    keyPressTimeRef.current = null;
    setBoostFactor(1);
  };

  // Handle keyboard input - use document-wide event handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys to avoid interfering with typing
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return;
      }

      // Check if the active element is an input or textarea
      const activeElement = document.activeElement;
      const isInputActive = activeElement.tagName === 'INPUT' ||
                           activeElement.tagName === 'TEXTAREA' ||
                           activeElement.isContentEditable;

      // Don't handle arrow keys if user is typing in an input
      if (isInputActive) {
        return;
      }

      // Start tracking key press time for boost calculation
      if (!keyPressTimeRef.current) {
        keyPressTimeRef.current = Date.now();
      }

      // Calculate boost factor based on how long key has been pressed
      // Non-linear boost that increases more dramatically over time
      const pressDuration = Date.now() - keyPressTimeRef.current;
      const newBoostFactor = Math.min(3, 1 + Math.pow(pressDuration / 1000, 1.5) * 0.5);
      setBoostFactor(newBoostFactor);

      // Map arrow keys to directions
      const directionMap = {
        'ArrowUp': 'up',
        'ArrowDown': 'down',
        'ArrowLeft': 'left',
        'ArrowRight': 'right'
      };

      movePlane(directionMap[e.key], true);
      e.preventDefault();
    };

    const handleKeyUp = (e) => {
      // Reset boost factor when key is released
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        keyPressTimeRef.current = null;
        setBoostFactor(1);
      }
    };

    // Add event listeners to the document
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // Auto-focus the keyboard catcher on mount
    if (keyboardCatcherRef.current) {
      keyboardCatcherRef.current.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []); // No dependencies needed since we're checking document.activeElement

  // Game loop - handles both movement and collisions
  useEffect(() => {
    if (gameWon) return;

    let lastTimestamp = 0;
    const gameLoop = (timestamp) => {
      // Calculate delta time for smooth animation
      const deltaTime = lastTimestamp ? (timestamp - lastTimestamp) / 16 : 1;
      lastTimestamp = timestamp;

      if (!gameContainerRef.current) {
        animationFrameRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      const { width, height } = gameContainerRef.current.getBoundingClientRect();

      // Move circles
      setCircles(prevCircles => {
        const updatedCircles = prevCircles.map(circle => {
          if (circle.collected) return circle;

          // Move circle from right to left with delta time for smooth movement
          // Apply boost factor to circle speed as well for a more dramatic effect
          const effectiveSpeed = circle.speed * gameSpeed * deltaTime * (boostFactor * 0.5 + 0.5);
          const newX = circle.x - effectiveSpeed;

          // If circle moves off screen, reset it to the right
          if (newX < -circle.size) {
            return createNewCircle(width, height, circle.id);
          }

          return { ...circle, x: newX };
        });

        return updatedCircles;
      });

      // Check for collisions
      setCircles(prevCircles => {
        const planeRadius = 10; // Half of plane size
        let collectedCount = 0;

        const collidedCircles = prevCircles.map(circle => {
          if (circle.collected) {
            collectedCount++;
            return circle;
          }

          const dx = planePosition.x + planeRadius - (circle.x + circle.size / 2);
          const dy = planePosition.y + planeRadius - (circle.y + circle.size / 2);
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < planeRadius + circle.size / 2 && circle.filled) {
            collectedCount++;
            return { ...circle, collected: true };
          }

          return circle;
        });

        // Update score
        setScore(collectedCount);

        // Update game speed based on score ranges
        if (collectedCount <= 20) {
          setGameSpeed(1);
        } else if (collectedCount <= 40) {
          setGameSpeed(2);
        } else if (collectedCount <= 60) {
          setGameSpeed(3);
        } else if (collectedCount <= 80) {
          setGameSpeed(4);
        } else {
          setGameSpeed(5);
        }

        // Check win condition
        if (collectedCount >= 100) {
          setGameWon(true);
        }

        return collidedCircles;
      });

      // Continue the game loop
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    // Start the game loop
    animationFrameRef.current = requestAnimationFrame(gameLoop);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameWon, planePosition, gameSpeed, boostFactor]); // Added boostFactor as dependency

  // Create fireworks when game is won
  useEffect(() => {
    if (!gameWon) return;

    const createFirework = () => {
      if (!gameContainerRef.current) return;

      const { width, height } = gameContainerRef.current.getBoundingClientRect();
      const centerX = Math.random() * width;
      const centerY = Math.random() * height;
      const particles = [];

      for (let i = 0; i < 20; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 40 + 10;

        particles.push({
          id: Date.now() + i,
          x: centerX + Math.cos(angle) * distance,
          y: centerY + Math.sin(angle) * distance,
          size: Math.floor(Math.random() * 2) * 4 + 4, // Pixelated sizes: 4 or 8
          opacity: Math.random() * 0.5 + 0.5
        });
      }

      setFireworks(prev => [...prev, ...particles]);

      // Remove old fireworks to prevent memory issues
      if (fireworks.length > 300) {
        setFireworks(prev => prev.slice(prev.length - 300));
      }
    };

    fireworkIntervalRef.current = setInterval(createFirework, 300);

    return () => {
      if (fireworkIntervalRef.current) {
        clearInterval(fireworkIntervalRef.current);
      }
    };
  }, [gameWon, fireworks]);

  // Reset game
  const resetGame = () => {
    if (gameContainerRef.current) {
      const { width, height } = gameContainerRef.current.getBoundingClientRect();
      const newCircles = [];

      for (let i = 0; i < 30; i++) {
        newCircles.push(createNewCircle(width, height, i));
      }

      setCircles(newCircles);
      setScore(0);
      setGameWon(false);
      setFireworks([]);
      setPlanePosition({ x: 50, y: 100 });
      setPlaneRotation(0);
      setGameSpeed(1);
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (gameContainerRef.current) {
        const { width, height } = gameContainerRef.current.getBoundingClientRect();

        // Update circle positions on resize
        setCircles(prevCircles => {
          return prevCircles.map(circle => {
            if (circle.x > width) {
              // If circle is now off-screen due to resize, bring it back
              return {
                ...circle,
                x: width + Math.random() * 100,
                y: Math.random() * height
              };
            }
            return circle;
          });
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle focus and blur
  const handleFocus = () => {
    if (keyboardCatcherRef.current) {
      keyboardCatcherRef.current.focus();
    }
  };

  const handleBlur = () => {
    if (keyboardCatcherRef.current) {
      keyboardCatcherRef.current.blur();
    }
  };

  // Handle click on game container
  const handleContainerClick = (e) => {
    if (gameWon) {
      resetGame();
    }
  };

  // Clean up touch interval on unmount
  useEffect(() => {
    return () => {
      if (touchIntervalRef.current) {
        clearInterval(touchIntervalRef.current);
      }
    };
  }, []);

  // Toggle controls visibility
  const toggleControls = () => {
    const newControlsState = !controlsVisible;
    setControlsVisible(newControlsState);

    // Toggle chat visibility by adding a custom event with the new state
    const event = new CustomEvent('toggleChat', {
      detail: {
        source: 'gamepadIcon',
        controlsVisible: newControlsState
      }
    });
    document.dispatchEvent(event);
  };

  // Add event listener for toggleGameControls
  useEffect(() => {
    const handleToggleGameControls = (event) => {
      // Set controls visibility based on the event detail
      setControlsVisible(event.detail.visible);
    };

    document.addEventListener('toggleGameControls', handleToggleGameControls);
    return () => {
      document.removeEventListener('toggleGameControls', handleToggleGameControls);
    };
  }, []);

  return (
    <>
      <GameContainer ref={gameContainerRef}>
        <KeyboardEventCatcher
          ref={keyboardCatcherRef}
          onClick={handleContainerClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
          tabIndex="0"
        />

        {!gameWon ? (
          <>
            <Plane
              style={{
                left: `${planePosition.x}px`,
                top: `${planePosition.y}px`,
                transform: `rotate(${planeRotation}deg) scale(${1 + (boostFactor - 1) * 0.2})` // Scale plane slightly with boost
              }}
            />

            {circles.map(circle => (
              !circle.collected && (
                <Circle
                  key={circle.id}
                  style={{ left: `${circle.x}px`, top: `${circle.y}px` }}
                  $size={circle.size}
                  $filled={circle.filled}
                />
              )
            ))}

            <ProgressBar $score={score} />
            <GameInstructions $controlsVisible={controlsVisible}>
              {isMobile ? 'Use on-screen controls to pick the filled dots' : 'Use arrow keys to pick the filled dots'}
            </GameInstructions>
          </>
        ) : (
          <>
            {fireworks.map(firework => (
              <Firework
                key={firework.id}
                $x={firework.x}
                $y={firework.y}
                $size={firework.size}
                $opacity={firework.opacity}
              />
            ))}
            <GameInstructions $controlsVisible={true}>
              You won! {isMobile ? 'Tap' : 'Click'} anywhere to play again.
            </GameInstructions>
          </>
        )}
      </GameContainer>

      {/* Gamepad icon for mobile */}
      {isMobile && (
        <GamepadIcon onClick={toggleControls}>
          +
        </GamepadIcon>
      )}

      {/* Render touch controls in a separate portal to ensure they're above everything */}
      {isMobile && controlsVisible && (
        <TouchControlsPortal>
          <TouchControls>
            <div></div>
            <TouchButton
              onTouchStart={() => handleTouchStart('up')}
              onTouchEnd={handleTouchEnd}
              onMouseDown={() => handleTouchStart('up')}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              style={{backgroundColor: activeTouchDirection === 'up' ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.95)',
                     color: activeTouchDirection === 'up' ? 'white' : 'var(--primary-color)'}}
            >
              ↑
            </TouchButton>
            <div></div>

            <TouchButton
              onTouchStart={() => handleTouchStart('left')}
              onTouchEnd={handleTouchEnd}
              onMouseDown={() => handleTouchStart('left')}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              style={{backgroundColor: activeTouchDirection === 'left' ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.95)',
                     color: activeTouchDirection === 'left' ? 'white' : 'var(--primary-color)'}}
            >
              ←
            </TouchButton>
            <div></div>
            <TouchButton
              onTouchStart={() => handleTouchStart('right')}
              onTouchEnd={handleTouchEnd}
              onMouseDown={() => handleTouchStart('right')}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              style={{backgroundColor: activeTouchDirection === 'right' ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.95)',
                     color: activeTouchDirection === 'right' ? 'white' : 'var(--primary-color)'}}
            >
              →
            </TouchButton>

            <div></div>
            <TouchButton
              onTouchStart={() => handleTouchStart('down')}
              onTouchEnd={handleTouchEnd}
              onMouseDown={() => handleTouchStart('down')}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              style={{backgroundColor: activeTouchDirection === 'down' ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.95)',
                     color: activeTouchDirection === 'down' ? 'white' : 'var(--primary-color)'}}
            >
              ↓
            </TouchButton>
            <div></div>
          </TouchControls>
        </TouchControlsPortal>
      )}
    </>
  );
};

export default PlaneGame;
